// プロフィールアイコン
<template>
  <div v-observe-visibility="vObserveVisibilityProps">
    <!-- アイコン無し -->
    <div v-if="hasIcon == false" :style="style" class="profile-icon" :hover="useHoverStyle">
      <div>{{ label }}</div>
    </div>

    <!-- アイコンロード後 -->
    <div
      v-else-if="bgImageLoad"
      class="profile-icon-image"
      :style="profileIconSize"
      :hover="useHoverStyle"
    >
      <img src="../assets/blank.gif" :style="bgImageStyle" />
    </div>

    <!-- アイコンロード用 (アイコンなし時と同じ) -->
    <div v-else :style="style" class="profile-icon" :hover="useHoverStyle">
      <div>{{ label }}</div>
    </div>
  </div>
</template>
<style scoped type="scss">
.profile-icon {
  position: relative;
  display: block;
  border-radius: 50%;
  text-align: center;
  color: white;
  overflow: hidden;

  &[hover]:hover {
    box-shadow: 0 0 0 4px #e9ecef;
    border-radius: 50%;
  }
}
.profile-icon > div {
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  margin: auto;
  height: 1em;
  line-height: 1;
}

.profile-icon-image {
  display: block;
  border-radius: 50%;
  overflow: hidden;

  &[hover]:hover {
    box-shadow: 0 0 0 4px #e9ecef;
  }
}

.profile-icon-for-load {
  display: none;
}
</style>

<script lang="ts">
import type { Attachment } from "../model";
import { computed, defineComponent, ref, watch, type PropType } from "vue";
import type { DirectIdStr } from "../direct-utility";
import DirectIconUtility from "./direct-icon-utility";
import S3AccessUtility from "./s3-acceess-utility";

type CssStyleObject = {
  [name: string]: string;
};

enum Status {
  LOADING, // ロード中
  LOADED, // ロード終了
}

export default defineComponent({
  name: "profile-icon",
  props: {
    // 話題ID
    topicId: { type: String, default: "" },
    /** ユーザー名(この最初の１文字が描画に利用される) */
    userName: { type: String, default: "" },
    /** ユーザーID。アイコン無時の背景色算出用 */
    id: { type: String as PropType<DirectIdStr>, default: () => "0" },
    /** hover時のスタイリング処理を行う場合は true を指定する */
    useHoverStyle: { type: Boolean, default: false },
    /**
     * 画像ファイル名
     * "" or "about:blank" : 画像指定無し
     * httpから始まる文字列: 外部リソース
     * その他              : S3上のリソース名
     */
    image: {
      type: [Object, String] as PropType<Attachment | string>,
      default: undefined,
    },
    // コンポーネントサイズ
    size: { type: Number, default: 35 },
    // アイコン画像が無い場合に出す、プロフィール名のフォントサイズ
    fontSize: { type: String, default: "" },
  },
  setup(props) {
    // image を S3上のリソースとして解決したもの
    // image が指定されない場合は ""
    // image に外部ファイルが指定された場合は、そのURL
    const s3image = ref("");

    // 初期はロード中
    const s3Status = ref<Status>(Status.LOADING);

    // 背景画像スタイル
    const bgImageStyleData = ref<CssStyleObject | undefined>({});

    // true: 画像ファイルがロードされた false: まだロードされてない
    const bgImageLoaded = ref(false);

    const errorCounter = ref(10); //!< ファイル読み込み時のエラーリトライ回数
    const errorRetrySecond = ref(5); //!< エラーリトライ回数の間隔(秒)
    const retryedReload = ref(false); // 画像取得のリトライ(一定インターバルによる取得)を行ったかのフラグ

    // ラベル
    const label = computed((): string => DirectIconUtility.label(props.userName));

    // 背景色
    const bgColor = computed((): string => DirectIconUtility.bgColor(props.id));

    // 背景画像として表示するもの
    const bgImage = computed((): string => {
      // S3からファイル情報ロード中の場合は image から判定。ロード後は s3image
      if (s3Status.value == Status.LOADING) {
        if (!props.image) {
          return "";
        } else if (typeof props.image == "string") {
          if (props.image == "" || props.image == "about:blank") {
            return "";
          } else {
            return props.image;
          }
        } else {
          return props.image.url;
        }
      } else {
        // Loaded の場合、s3image が解決済み
        return s3image.value;
      }
    });

    // アイコン画像が無い場合のスタイル
    const style = computed((): CssStyleObject => {
      const css: CssStyleObject = {
        "background-color": bgColor.value,
        width: `${props.size}px`,
        height: `${props.size}px`,
      };
      if (props.fontSize) css["font-size"] = props.fontSize;
      return css;
    });

    // 背景画像スタイル
    const bgImageStyle = computed((): CssStyleObject => {
      return bgImageStyleData.value ? bgImageStyleData.value : style.value;
    });

    // アイコン画像がある場合のスタイル
    const profileIconSize = computed((): CssStyleObject => {
      return {
        width: `${props.size}px`,
        height: `${props.size}px`,
      };
    });

    // アイコン画像を持つかどうか。true: 持つ false: 持たない(or S3ファイル情報ロード中)
    const hasIcon = computed((): boolean => {
      if (s3Status.value == Status.LOADING) return false;
      return s3image.value !== "";
    });

    // 背景画像URLがロードされたかどうか。true: ロードされた false: されてない(or S3ファイル情報ロード中)
    const bgImageLoad = computed((): boolean => {
      if (s3Status.value == Status.LOADING) return false;
      return bgImageLoaded.value;
    });

    // true: 画像ロードエラー
    const isLoadError = computed((): boolean => errorCounter.value < 0);

    // 画像ロードエラー処理
    const loadError = (): void => {
      s3image.value = ""; // エラー
      s3Status.value = Status.LOADED;
      bgImageLoaded.value = true;
      errorCounter.value -= 1;

      // ロードエラーになった回数が規定を下回り、まだ一定インターバルのリロードを行っていない時
      if (isLoadError.value == false && !retryedReload.value) {
        retryedReload.value = true;

        // 一定インターバルで画像をリロードを行う
        setTimeout(reloadImage, errorRetrySecond.value * 1000);
      }
    };

    // imgタグを内部で作成、画像ロードしてデータ取得
    const imageLoad = (file: string): void => {
      // 実ロード
      const img = new Image();
      img.onload = () => {
        const croppedStyle = DirectIconUtility.cropImage(img, props.size, props.size);
        bgImageStyleData.value = croppedStyle;
        bgImageLoaded.value = true;
      };
      img.onerror = loadError;
      img.src = file;
    };

    // プロフィール画像のリロード処理
    const reloadImage = async (): Promise<void> => {
      s3image.value = "";
      s3Status.value = Status.LOADING;
      bgImageStyleData.value = {};
      bgImageLoaded.value = false;

      if (!bgImage.value) {
        // アイコン無し → S3ロード終わり
        s3image.value = bgImage.value;
        s3Status.value = Status.LOADED;
        bgImageLoaded.value = true;
      } else if (bgImage.value.startsWith("blob")) {
        // blobなのでロード済み
        s3image.value = bgImage.value;
        s3Status.value = Status.LOADED;
        imageLoad(s3image.value);
      } else if (bgImage.value.startsWith("http")) {
        // 外部のURL(direct の profile画像など) → S3ロード終わり
        s3image.value = bgImage.value;
        s3Status.value = Status.LOADED;

        imageLoad(s3image.value);
      } else {
        // ファイル名だけが書かれている場合は S3 で解決してみる
        const s3 = S3AccessUtility.getInstance();
        const file = await s3.getThumbFile(
          bgImage.value,
          { topicId: props.topicId, messageId: "", commentId: "" },
          "image/jpg"
        );

        if (typeof file == "string") {
          s3image.value = file;
          s3Status.value = Status.LOADED;

          imageLoad(s3image.value);
        } else {
          loadError();
        }
      }
    };

    // v-observer-visibility の処理
    const visibilityChanged = (isVisible: boolean /*, entry: unknown*/): void => {
      if (isVisible == true) {
        // 画面内に入るときだけロード処理に入る(event が invoke:once なので再度 true に入った場合は処理されない)
        reloadImage();
      }
    };

    // v-observe-visibility の利用設定
    // １度だけ発火＆画面が100px以内になると実行
    const vObserveVisibilityProps = {
      callback: visibilityChanged,
      once: true,
      intersection: {
        rootMargin: "100px",
      },
    };

    watch(
      () => props.image,
      (
        newImage: Attachment | string | undefined,
        oldImage: Attachment | string | undefined
      ): void => {
        if (!bgImage.value) {
          reloadImage();
        } else {
          const getUrl = (image: Attachment | string | undefined): string => {
            if (image == undefined) return "";
            if (typeof image == "string") return image;
            return image.url;
          };
          if (oldImage && getUrl(newImage) != getUrl(oldImage)) {
            // 旧画像があり、別のもの(無しでもOK)に切り替わる場合のみリロードする
            reloadImage();
          }
        }
      },
      { immediate: true }
    );

    return {
      label,
      style,
      bgImageStyle,
      profileIconSize,
      hasIcon,
      bgImageLoad,
      vObserveVisibilityProps,
    };
  },
});
</script>
